/**
 * DriverPortal · /driver/:token
 *
 * Lightweight driver PWA dashboard. Shows all active pickups grouped by day,
 * lets driver tap through to each carrier handoff view, and manages web-push
 * subscription.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import PWAInstallButton from '../components/PWAInstallButton';
import OfflineIndicator from '../components/OfflineIndicator';
import {
  Truck, CalendarBlank, BellRinging, BellSlash, Warning,
  MapPin, Package, Clock, CaretRight, CheckCircle,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// base64-url → Uint8Array for applicationServerKey
const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const GROUP_META = {
  today:     { label: 'Today',              accent: 'bg-amber-50 border-amber-300 text-amber-900' },
  tomorrow:  { label: 'Tomorrow',           accent: 'bg-blue-50 border-blue-200 text-blue-900' },
  this_week: { label: 'Later this week',    accent: 'bg-muted border-border text-foreground' },
  later:     { label: 'Later',              accent: 'bg-muted border-border text-muted-foreground' },
  no_date:   { label: 'Undated',            accent: 'bg-muted border-border text-muted-foreground' },
};

const STATUS_BADGE = {
  planned: 'bg-muted text-foreground',
  confirmed: 'bg-blue-100 text-blue-700',
  at_yard: 'bg-indigo-100 text-indigo-700',
  at_dock: 'bg-cyan-100 text-cyan-700',
  loading: 'bg-amber-100 text-amber-800',
  loaded: 'bg-yellow-100 text-yellow-800',
  sealed: 'bg-emerald-100 text-emerald-800',
  dispatched: 'bg-teal-100 text-teal-800',
};

export default function DriverPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushState, setPushState] = useState({ supported: false, permission: 'default', subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/drivers/${token}/dashboard`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load your pickups');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  // Detect current push subscription state
  useEffect(() => {
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        setPushState({ supported: false, permission: 'default', subscribed: false });
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushState({
          supported: true,
          permission: Notification.permission,
          subscribed: !!sub,
        });
      } catch {
        setPushState({ supported: true, permission: Notification.permission, subscribed: false });
      }
    })();
  }, []);

  const subscribePush = async () => {
    if (!pushState.supported) {
      toast.error('Push is not supported on this browser');
      return;
    }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error('Notification permission declined');
        setPushState(s => ({ ...s, permission: perm }));
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const vapid = data?.vapid_public_key;
      if (!vapid) {
        toast.error('Server push key missing');
        return;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid),
        });
      }
      await axios.post(`${API}/drivers/${token}/push/subscribe`, {
        endpoint: sub.endpoint,
        keys: sub.toJSON().keys,
        user_agent: navigator.userAgent?.slice(0, 180),
      });
      setPushState({ supported: true, permission: 'granted', subscribed: true });
      toast.success('Push notifications enabled');
    } catch (e) {
      toast.error(e?.message || 'Subscribe failed');
    } finally { setPushBusy(false); }
  };

  const unsubscribePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${API}/drivers/${token}/push/unsubscribe`, { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setPushState(s => ({ ...s, subscribed: false }));
      toast.success('Notifications disabled');
    } catch (e) {
      toast.error(e?.message || 'Unsubscribe failed');
    } finally { setPushBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <p className="text-muted-foreground font-mono text-sm">Loading your pickups…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card border border-rose-300 p-6 text-center" data-testid="driver-error">
          <Warning size={40} weight="duotone" className="text-rose-600 mx-auto mb-3" />
          <h1 className="text-lg font-bold tracking-tight mb-1">Link unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const driver = data?.driver;
  const groups = data?.groups || {};
  const total = data?.total_active_pickups || 0;

  return (
    <div className="min-h-screen bg-muted" data-testid="driver-portal-page">
      <Toaster position="top-center" richColors />
      <OfflineIndicator />

      <div className="bg-amber-400 text-amber-950 text-center py-1.5 px-4 text-xs font-bold uppercase tracking-widest border-b-2 border-amber-500">
        <Truck size={14} weight="bold" className="inline mr-1" />
        Driver Portal · Signed link
      </div>

      <header className="bg-brand text-white border-b-4 border-amber-400">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] opacity-70">Stockaris WMS</p>
            <h1 className="text-2xl font-bold">
              {driver?.name ? `Hi, ${driver.name.split(' ')[0]}` : 'Driver Portal'}
            </h1>
            <p className="text-[11px] opacity-70 mt-0.5 font-mono">
              {total} active pickup{total === 1 ? '' : 's'}
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="driver-pwa-install">
            <div className="bg-card/20 hover:bg-card/30 transition rounded-none">
              <PWAInstallButton compact />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Push notification card */}
        <div className="bg-card border border-border rounded-lg p-3 flex items-start gap-3" data-testid="driver-push-card">
          <div className={`p-2 ${pushState.subscribed ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
            {pushState.subscribed ? <BellRinging size={18} weight="fill" /> : <BellSlash size={18} weight="bold" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold tracking-tight">
              {pushState.subscribed ? 'Notifications on' : 'Turn on pickup alerts'}
            </p>
            <p className="text-xs text-muted-foreground">
              {pushState.supported
                ? 'Get a notification the moment a new dispatch is assigned to you.'
                : 'This browser doesn\'t support push notifications. Install to Home Screen for the full experience.'}
            </p>
          </div>
          {pushState.supported && (
            pushState.subscribed ? (
              <Button size="sm" variant="outline" onClick={unsubscribePush} disabled={pushBusy}
                data-testid="driver-push-unsubscribe"
                className="rounded-none">
                Disable
              </Button>
            ) : (
              <Button size="sm" onClick={subscribePush} disabled={pushBusy}
                data-testid="driver-push-subscribe"
                className="bg-brand text-white rounded-none hover:bg-[#0040d9]">
                {pushBusy ? '…' : 'Enable'}
              </Button>
            )
          )}
        </div>

        {/* Empty state */}
        {total === 0 && (
          <div className="bg-card border border-border rounded-lg p-10 text-center" data-testid="driver-empty">
            <Package size={40} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No active pickups assigned to you right now.</p>
            <p className="text-[11px] text-muted-foreground mt-1 font-mono">We'll notify you the moment something lands.</p>
          </div>
        )}

        {/* Grouped pickups */}
        {Object.entries(groups).map(([key, rows]) => {
          if (!rows || rows.length === 0) return null;
          const meta = GROUP_META[key] || GROUP_META.later;
          return (
            <div key={key} data-testid={`driver-group-${key}`}>
              <div className={`inline-flex items-center gap-2 px-3 py-1 border text-[11px] font-bold uppercase tracking-widest mb-2 ${meta.accent}`}>
                <CalendarBlank size={13} weight="bold" />
                {meta.label} · {rows.length}
              </div>
              <div className="space-y-2">
                {rows.map((p) => (
                  <Link
                    key={p.token}
                    to={`/carrier/${p.token}`}
                    data-testid={`driver-pickup-${p.dispatch_number}`}
                    className="block bg-card border border-border rounded-lg p-3 hover:border-brand transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-bold">{p.dispatch_number}</span>
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_BADGE[p.status] || 'bg-muted text-foreground'}`}>
                            {p.status?.replace('_', ' ')}
                          </span>
                          {p.pop_submitted && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 inline-flex items-center gap-0.5">
                              <CheckCircle size={10} weight="fill" /> POP
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-foreground mt-0.5 truncate">{p.customer_name || '—'}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-muted-foreground font-mono">
                          {p.destination && (
                            <span className="flex items-center gap-0.5"><MapPin size={10} /> {p.destination}</span>
                          )}
                          {p.container_number && (
                            <span className="flex items-center gap-0.5"><Package size={10} /> {p.container_number}</span>
                          )}
                          {(p.dock_start_at || p.planned_dispatch_at) && (
                            <span className="flex items-center gap-0.5"><Clock size={10} />
                              {new Date(p.dock_start_at || p.planned_dispatch_at).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                              {p.dock_code && ` · ${p.dock_code}`}
                            </span>
                          )}
                        </div>
                        {p.note && (
                          <p className="text-[11px] text-muted-foreground mt-1 italic">“{p.note}”</p>
                        )}
                      </div>
                      <CaretRight size={18} className="text-slate-300 mt-1" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}

        <footer className="text-center text-[10px] text-muted-foreground font-mono pt-4 pb-8">
          Powered by Stockaris WMS · Auto-refreshes every 60s.
        </footer>
      </main>
    </div>
  );
}
